import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import InternalLinks from '../../components/Navigation/InternalLinks';
import PricingComparisonSection from '../../components/pricing/PricingComparisonSection';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  DocumentTextIcon,
  ShieldCheckIcon,
  ClockIcon,
  UserGroupIcon,
  AcademicCapIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  CpuChipIcon,
  DevicePhoneMobileIcon,
  PlayIcon,
  CogIcon,
  LockClosedIcon,
  StarIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  EyeIcon,
  PencilSquareIcon,
  CalendarIcon,
  UserIcon,
  ArrowPathIcon,
  XMarkIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const DentalConsentSoftware = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);

  // Set page meta
  usePageMeta(
    'Dental Consent Software for UK Practices | Smile Consent',
    'Dental consent software for UK practices with AI-assisted drafting (clinician-reviewed), digital signing, treatment workflows, and consent activity records.',
    'https://smileconsent.co.uk/dental-consent-software'
  );

  // GSAP animations - disabled to prevent fading issues
  useEffect(() => {
    // Scroll-triggered animations only
    gsap.utils.toArray('.animate-on-scroll').forEach((element) => {
      gsap.fromTo(element, 
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: element,
            start: 'top 80%',
            end: 'bottom 20%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    });
  }, []);

  const handleScheduleDemo = () => {
    navigate('/book-demo');
  };

  const softwareFeatures = [
    {
      icon: DocumentTextIcon,
      title: 'AI-assisted consent',
      description: 'Generate an editable draft in minutes; your team approves before send',
      features: [
        'AI-assisted consent drafting',
        'Patient search (name, email, DOB, address)',
        'Multiple treatments per consent',
        'Digital patient signatures',
        'Consent activity records where provided'
      ]
    },
    {
      icon: ShieldCheckIcon,
      title: 'Documentation support',
      description: 'Records and workflows to support your own compliance processes — not automated certification',
      features: [
        'Themes aligned with common UK expectations',
        'Materials for your own inspection prep',
        'Consistent team workflow',
        'You retain legal and regulatory responsibility',
        'Product updates over time'
      ]
    },
    {
      icon: ClockIcon,
      title: 'Time-Saving Automation',
      description: 'Streamlined consent processes that save time and reduce errors',
      features: [
        'Generate consent in under a minute',
        'Patient search & multiple treatments',
        'Review step before send',
        'Review, edit, then send to sign',
        'Reduced paperwork time'
      ]
    },
    {
      icon: UserGroupIcon,
      title: 'Team Collaboration',
      description: 'Seamless collaboration between dental team members',
      features: [
        'Multi-user access',
        'Role-based permissions',
        'Real-time updates',
        'Team notifications',
        'Shared consent workflow'
      ]
    },
    {
      icon: CpuChipIcon,
      title: 'Advanced Analytics',
      description: 'Comprehensive reporting and analytics dashboard',
      features: [
        'Consent completion rates',
        'Compliance metrics',
        'Form usage analytics',
        'Performance dashboards',
        'Custom reports'
      ]
    },
    {
      icon: CogIcon,
      title: 'System Integration',
      description: 'Seamless integration with existing practice systems',
      features: [
        'Practice workflow alignment',
        'Digital workflow automation',
        'Secure export & reporting',
        'Consistent templates across your team',
        'Encrypted data handling'
      ]
    }
  ];

  const softwareStats = [
    {
      number: '< 1 min',
      label: 'Per consent',
      description: 'AI-assisted draft, clinician-reviewed'
    },
    {
      number: '99.9%',
      label: 'Uptime Guarantee',
      description: 'Reliable software'
    },
    {
      number: '24/7',
      label: 'Support Available',
      description: 'Expert assistance'
    },
    {
      number: '100%',
      label: 'Human review',
      description: 'Before AI-assisted sends to patients'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Hero Section */}
      <section ref={heroRef} className="bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Dental Consent Software for UK Practices
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
              Dental consent software for UK practices with AI-assisted consent creation, digital signing, treatment workflows, and audit trails in one platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-blue-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <SparklesIcon className="h-6 w-6" />
                Start Free Trial
              </Link>
              <button 
                onClick={handleScheduleDemo}
                className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
              >
                <CalendarIcon className="h-6 w-6" />
                Book Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Software Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {softwareStats.map((stat, index) => (
              <div key={index} className="text-center animate-on-scroll">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-lg font-semibold text-gray-900 mb-1">
                  {stat.label}
                </div>
                <div className="text-sm text-gray-600">
                  {stat.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Software Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Dental Consent Software Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              AI-assisted consent drafting with clinician review, patient search, multiple treatments per consent, digital signing, and activity records — your practice remains responsible for regulatory compliance.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {softwareFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow animate-on-scroll">
                <div className="flex items-center mb-4">
                  <div className="bg-blue-100 p-3 rounded-lg mr-4">
                    <feature.icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6">
                  {feature.description}
                </p>
                <ul className="space-y-2">
                  {feature.features.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center text-sm text-gray-600">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PricingComparisonSection
        subtitle="All plans include unlimited users — no per-user fees like our competitors!"
      />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-500 to-indigo-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Dental Practice?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Join thousands of dental practices using our comprehensive consent software. 
            Start your free trial today - no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-white text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <SparklesIcon className="h-6 w-6" />
              Start Free Trial
            </Link>
            <button 
              onClick={handleScheduleDemo}
              className="border-2 border-white text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
            >
              <CalendarIcon className="h-6 w-6" />
              Book Demo
            </button>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <InternalLinks
            title="Popular Dental Consent Software Searches"
            links={[
              {
                title: 'Implant consent form UK',
                description: 'Treatment-specific implant consent with digital signing',
                path: '/implant-consent-form-uk',
              },
              {
                title: 'Root canal consent form UK',
                description: 'Endodontic consent with alternatives and prognosis',
                path: '/root-canal-consent-form-uk',
              },
              {
                title: 'Composite bonding consent form UK',
                description: 'Cosmetic consent wording for bonding cases',
                path: '/composite-bonding-consent-form-uk',
              },
              {
                title: 'Veneer consent form UK',
                description: 'Consent records for veneer treatment planning',
                path: '/veneer-consent-form-uk',
              },
            ]}
          />
        </div>
      </section>
    </div>
  );
};

export default DentalConsentSoftware;
